import type { BoxProps, DividerProps } from '@mui/material';
import { alpha, Box, Divider, styled } from '@mui/material';
import type { DataGridProProps } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  CUSTOM_ERROR_COLOR_LIGHT,
  CUSTOM_WARNING_COLOR_LIGHT
} from 'constants/colors';

const generateNoDashedBorderColumns = (columnsWithNoDashedBorder: string[]) => {
  return columnsWithNoDashedBorder
    .map((col) => `[data-field="${col}"]`)
    .join(', ');
};

export const StyledRosterPlanDataGridPro = styled(DataGridPro, {
  shouldForwardProp: (prop) =>
    prop !== 'firstColumnName' && prop !== 'columnsWithNoDashedBorder'
})<
  DataGridProProps & {
    firstColumnName: string;
    columnsWithNoDashedBorder?: string[];
  }
>(({ theme, firstColumnName, columnsWithNoDashedBorder = [] }) => {
  const columnsSelector = generateNoDashedBorderColumns(
    columnsWithNoDashedBorder
  );

  const dashedBorder = `repeating-linear-gradient(
    to bottom,
    ${theme.vars.palette.outline.variant} 0,
    ${theme.vars.palette.outline.variant} 4px,
    transparent 4px,
    transparent 8px
  ) 1`;

  const solidBorder = `repeating-linear-gradient(
    to bottom,
    ${theme.vars.palette.outline.variant} 0,
    ${theme.vars.palette.outline.variant} 4px,
    transparent 4px,
    transparent 4px
  ) 1`;

  const commonRightBorderDashed = {
    borderImage: dashedBorder,
    borderImageSlice: 1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid'
  };

  // Note: we do not use the "normal" solid border because this will not align with the dashed border
  const commonRightBorderSolid = {
    borderImage: solidBorder,
    borderImageSlice: 1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid'
  };

  return {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.vars.palette.outline.variant,
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.04)',
    flex: 1,
    minHeight: 0,

    '.MuiDataGrid-row': {
      borderBottom: `1px solid ${theme.vars.palette.outline.variant}`
    },

    // header background
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader, .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller':
      {
        backgroundColor: theme.vars.palette.common.white
      },

    '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor, & .MuiDataGrid-columnHeaders .MuiDataGrid-row--borderBottom .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--header':
      {
        borderBottom: 0
      },

    // remove border from first column header
    [`& .MuiDataGrid-columnHeader[data-field="${firstColumnName}"]`]: {
      borderLeft: 'none',
      borderImage: 'none'
    },

    // cell borders with right dashed border except for specified columns
    [`& .MuiDataGrid-cell:not(${columnsSelector})`]: {
      borderTop: 'none',
      borderLeft: 'none',
      ...commonRightBorderDashed
    },

    // cell borders with NO right dashed border
    [`& .MuiDataGrid-cell:is(${columnsSelector})`]: {
      borderTop: 'none'
    },

    // add solid right border to first column cells
    [`& .MuiDataGrid-cell[data-field="${firstColumnName}"], .MuiDataGrid-columnHeader[data-field="${firstColumnName}"]`]:
      {
        borderRight: 'none',
        ...commonRightBorderSolid
      },

    // column header and row separators
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: `3px solid ${theme.palette.primary.main}`
    },

    '& .MuiDataGrid-columnSeparator': {
      display: 'none'
    },

    // remove bottom border from last visible row
    '& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell': {
      borderBottom: 'none'
    },

    // striped rows
    '& .stripedRow-even': {
      backgroundColor: theme.vars.palette.grey?.[50]
    },

    // send to flex row
    '& .send-to-flex-row': {
      backgroundColor: theme.vars.palette.surface.dim
    },

    // updated roster match row
    '& .roster-match-row': {
      backgroundColor: CUSTOM_WARNING_COLOR_LIGHT
    },

    // parked row
    '& .parked-row': {
      backgroundColor: CUSTOM_ERROR_COLOR_LIGHT
    },

    // weekend styling
    '& .saturday-header, & .sunday-header': {
      background: `
    linear-gradient(
      rgba(${theme.vars.palette.outline.mainChannel} / 0.08),
      rgba(${theme.vars.palette.outline.mainChannel} / 0.08)
    ),
    white
  `
    },

    '& .saturday-header': {
      borderTopLeftRadius: theme.shape.borderRadius
    },

    '& .sunday-header': {
      borderTopRightRadius: theme.shape.borderRadius,
      ...commonRightBorderDashed
    },

    '& .last-day-header': {
      ...commonRightBorderDashed
    },

    '& .weekend': {
      backgroundColor: `rgba(${theme.vars.palette.outline.mainChannel} / 0.08)`
    },

    '& .MuiDataGrid-cell.today': {
      backgroundColor: alpha(theme.palette.primary.light, 0.1)
    },

    '& .MuiDataGrid-cell.day-cell': {
      padding: 0,
      display: 'flex',
      flexDirection: 'column'
    },

    // pinned columns
    '& .MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-cell--pinnedRight, .MuiDataGrid-columnHeader--pinnedRight':
      {
        backgroundColor: theme.palette.background.paper
      },

    '& .stripedRow-even .MuiDataGrid-cell--pinnedLeft, .stripedRow-even .MuiDataGrid-columnHeader--pinnedLeft, & .stripedRow-even .MuiDataGrid-cell--pinnedRight, .stripedRow-even .MuiDataGrid-columnHeader--pinnedRight':
      {
        backgroundColor: theme.vars.palette.grey?.[50]
      },

    // .MuiDataGrid-columnHeader--withLeftBorder

    '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup.MuiDataGrid-columnHeader--last, & .last-day-header, & .MuiDataGrid-row .day-cell:last-of-type':
      {
        borderRight: 'none',
        borderImage: 'none'
      },

    // pinned columns: send to flex row
    '& .send-to-flex-row .MuiDataGrid-cell--pinnedLeft, .send-to-flex-row .MuiDataGrid-columnHeader--pinnedLeft':
      {
        backgroundColor: theme.vars.palette.surface.dim
      },

    // pinned columns: updated roster match row
    '& .roster-match-row .MuiDataGrid-cell--pinnedLeft, .roster-match-row .MuiDataGrid-columnHeader--pinnedLeft':
      {
        backgroundColor: CUSTOM_WARNING_COLOR_LIGHT
      },

    // pinned columns: parked row
    '& .parked-row .MuiDataGrid-cell--pinnedLeft, .parked-row .MuiDataGrid-columnHeader--pinnedLeft':
      {
        backgroundColor: CUSTOM_ERROR_COLOR_LIGHT
      },

    // week number row
    '& .MuiDataGrid-columnHeaderTitleContainer.MuiDataGrid-withBorderColor': {
      borderBottom: 0
    },

    '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup':
      {
        ...commonRightBorderDashed
      },

    '& .week-header-row > div:first-of-type': {
      height: '34px !important'
    },

    '& .week-header-row > div:first-of-type .MuiDataGrid-columnHeaderTitleContainer.MuiDataGrid-withBorderColor':
      {
        justifyContent: 'center'
      },

    '& .MuiDataGrid-withBorderColor': {
      borderColor: theme.vars.palette.outline.variant
    },

    '& .MuiDataGrid-scrollbar--vertical': {
      top: 'calc(23.3 * var(--mui-spacing, 4px))',
      height:
        'calc(var(--DataGrid-hasScrollY) * (100% - 90px - var(--DataGrid-bottomContainerHeight) - var(--DataGrid-hasScrollX) * var(--DataGrid-scrollbarSize)))'
    },

    // open requests specific styles
    '&.open-requests-grid .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup':
      {
        border: 'none'
      },

    '&.open-requests-grid .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup[data-fields="|-commitmentHours-|"]':
      {
        borderRight: 'none',
        borderImage: 'none'
      },

    '&.open-requests-grid .MuiDataGrid-scrollbar--vertical': {
      top: 'calc(14.7 * var(--mui-spacing, 4px))',
      height:
        'calc(var(--DataGrid-hasScrollY) * (100% - 58px - var(--DataGrid-bottomContainerHeight) - var(--DataGrid-hasScrollX) * var(--DataGrid-scrollbarSize)))'
    },

    '&.open-requests-grid .MuiDataGrid-columnHeaders > div:nth-of-type(2)': {
      display: 'none'
    },

    '&.open-requests-grid .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup[data-fields="|-commitmentHours-|"] .MuiDataGrid-columnHeaderDraggableContainer .MuiDataGrid-columnHeaderTitleContainer':
      {
        display: 'flex',
        justifyContent: 'center'
      },

    '&.open-requests-grid .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup.MuiDataGrid-columnHeader--last':
      {
        borderRight: 'none',
        borderImage: 'none'
      },

    '&.open-requests-grid .MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--withRightBorder.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--filledGroup.MuiDataGrid-columnHeader--pinnedLeft.MuiDataGrid-columnHeader--last':
      {
        ...commonRightBorderSolid
      },

    '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeader--emptyGroup.MuiDataGrid-columnHeader--pinnedLeft.MuiDataGrid-columnHeader--last':
      {
        ...commonRightBorderSolid
      },

    '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderTitle': {
      fontFamily: 'SourceSansPro-SemiBold',
      fontSize: '14px'
    },

    '& .MuiDataGrid-virtualScrollerContent ': {
      paddingBottom: theme.spacing(2.5)
    },

    '& .MuiDataGrid-filler > div': {
      backgroundColor: 'transparent',
      borderTop: 'none',
      borderRight: 'none'
    },

    '& .MuiDataGrid-cell--textCenter': {
      justifyContent: 'start'
    },

    '&.MuiDataGrid-root': {
      border: `1px solid ${theme.vars.palette.outline.variant}`
    },

    '& .MuiDataGrid-cell.day-cell.last-day-cell': {
      borderRight: 'none',
      borderImage: 'none'
    }
  };
});

export const StyledResizeHandleBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  transition: 'all 0.3s ease-in-out',

  '&:hover': { opacity: 1 },

  '& .MuiDivider-wrapper': {
    svg: {
      width: '24px',
      height: '100%',
      color: theme.palette.grey[500]
    }
  },

  '& .MuiDivider-root::before, & .MuiDivider-root::after': {
    borderTopColor: theme.palette.divider,
    transition: 'all 0.3s ease-in-out'
  },

  '&[data-resize-handle-active] .MuiDivider-root::before, &[data-resize-handle-active] .MuiDivider-root::after':
    {
      borderTopColor: theme.palette.divider
    },

  '&:active .MuiDivider-root::before, &:active .MuiDivider-root::after': {
    borderTopColor: theme.palette.primary.main,
    boxShadow: `0 0 2px 1px ${alpha(theme.palette.primary.main, 0.16)}`
  },

  '&:active': {
    svg: {
      color: theme.palette.primary.main
    }
  }
}));

/**
 * The screen and the automatic-rostering explanation side by side.
 *
 * The panel is a sibling rather than an overlay: the planner reads the grid while asking about it, so
 * covering the grid would defeat the point. It sits beside the *toolbar* too, not only beside the
 * grids - which is what makes the toolbar give up width when the panel opens, collapsing its
 * right-hand icons into the overflow menu instead of letting them run under the panel.
 */
export const StyledRosterPlanWorkspaceBox = styled(Box)<BoxProps>(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(3),
    minHeight: 0
  })
);

/** Everything but the panel: the toolbar and the grids under it. */
export const StyledRosterPlanMainBox = styled(Box)<BoxProps>(() => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0
}));

export const StyledPanelsContainersBox = styled(Box)<BoxProps>(() => ({
  height: 'calc(100vh - 220px)', // Note: adjust toolbar height if required
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0
}));

export const StyledPanelBox = styled(Box)<BoxProps>(() => ({
  flex: 1,
  minHeight: 0,
  display: 'flex'
}));

export const StyledResizeHandleDivider = styled(Divider)<DividerProps>(() => ({
  width: '100%',

  '& .MuiDivider-wrapper': {
    display: 'flex'
  }
}));
